import { dataTypeTests, Operator } from "@/types/insights/operators"
import { AdvancedSearchFilter, CaseDetails, DataType, Field } from "@/types"

import { get, StringIterator } from "lodash"
import { SearchClauseParameters } from "@/types/view"
import { CalculationResults, FilteredCalculationResults } from "@/types/insights/calculatedResults"
import { ClientSideCalculations, GroupedSearchResults } from "@/types/insights/clientSideCalculations"
import { LabelFormat, Insight } from "@/types/insights"

const addADay = (date: Date) => new Date(date.getTime() + (60 * 60 * 24 * 1000))
const getDynamicDate = (value: any, dynamic: boolean, operator: Operator, params?: SearchClauseParameters) => {
	if (value instanceof Date) {
		if (operator === Operator.Equals) {
			return {
				start: value,
				end: addADay(value)
			}
		}

		return value
	}

	let currentDate: Date | null = null
	if (dynamic) {
		let now = new Date(Date.now())
		currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
		switch (value) {
			case "NextWeek":
			case "PreviousWeek":
			case "CurrentWeek":
				const dayOfWeek = now.getDay()

				// 1 = Monday
				const startDay = params && (params.startDayOfWeek !== undefined)
					? params.startDayOfWeek
					: 1

				let dayChange = startDay - dayOfWeek
				if (dayChange > 0) {
					dayChange -= 7
				}

				if (value === "NextWeek") {
					dayChange += 7
				} else if (value === "PreviousWeek") {
					dayChange -= 7
				}

				currentDate.setDate(now.getDate() + dayChange)
				break
			case "CurrentMonth":
				const dayOfMonth = now.getDate()
				currentDate.setDate(now.getDate() - dayOfMonth + 1)
				break
			case "CurrentQuarter":
				const quarter = Math.floor(now.getMonth() / 3) + 1
				currentDate = new Date(now.getFullYear(), (quarter - 1) * 3, 1)
				break
			default:
				const days = parseInt(value)
				if (!isNaN(days)) {
					currentDate.setDate(now.getDate() + days)
				}
				break
		}
	} else {
		currentDate = new Date(value)
	}

	if (operator === Operator.Equals || operator === Operator.OnDay) {
		return {
			start: currentDate,
			end: addADay(currentDate)
		}
	}

	if (operator === Operator.DateRange) {
		return {
			start: params!.startDate,
			end: params!.endDate
		}
	}

	return currentDate
}

export const filterMixin = {
	methods: {
		filtersToFunctions (filters: AdvancedSearchFilter[], currentUserId: string) {
			return filters
				.filter(f => f.orFilters || (dataTypeTests[f.column!.fieldDataType!] !== undefined && dataTypeTests[f.column!.fieldDataType!]![f.operator!] !== undefined))
				.map(f => {
					if (f.orFilters) {
						const orFilterFunctions: ((record: CaseDetails) => boolean)[] = this.filtersToFunctions(f.orFilters, currentUserId)
						return (record: CaseDetails) => orFilterFunctions.some(f => f(record))
					} else {
						let testValue: any = f.value

						if (f.column!.fieldDataType === DataType.dateTime) {
							testValue = getDynamicDate(f.value,
								f.dynamicValue === undefined ? false : f.dynamicValue,
								f.operator === undefined ? Operator.Equals : f.operator,
								f.searchClauseParameters)
						} else if (f.column!.fieldDataType === DataType.timeSpentAsValue) {
							if (f.searchClauseParameters && f.searchClauseParameters.timeSpentClause) {
								testValue = {
									hours: f.searchClauseParameters.timeSpentClause.hoursSpent,
									searchValueLabel: f.searchClauseParameters.timeSpentClause.searchLabel
								}
							} else {
								return (record: CaseDetails) => true
							}
						} else if (f.dynamicValue && f.value === "[Me]") {
							testValue = currentUserId
						} else if (f.column!.fieldDataType === DataType.string) {
							testValue = f.value ? f.value.toLocaleLowerCase() : f.value
						}

						const dataTypeOperator = dataTypeTests[f.column!.fieldDataType!]![f.operator!]!
						return (record: CaseDetails) => {
							// if there are no fields it is likely because the all the record's user-defined fields in the 'show columns' are null
							// in this case, filter the same way as the server side treats a null field value - depending on the operator
							// e.g. Any(null) - don't match, Contains(null) - don't match, NotEqual(null) - match, etc
							if (f.column!.dynamicField && !record.fields) {
								if (f.operator! === Operator.Any ||
									f.operator! === Operator.Contains ||
									f.operator! === Operator.Equals ||
									f.operator! === Operator.GreaterThan ||
									f.operator! === Operator.LessThan ||
									f.operator! === Operator.OnDay ||
									f.operator! === Operator.Within ||
									f.operator! === Operator.InTeam ||
									f.operator! === Operator.IsSet) {
									return false
								} else {
									return true
								}
							}

							return dataTypeOperator(testValue,
								f.column!.dynamicField
									? record.fields[f.column!.fieldId!]
										? record.fields[f.column!.fieldId!].value
										: null
									: (record as any)[f.column!.key!]
							)
						}
					}
				})
		},
		filterList (list: any[], filters: AdvancedSearchFilter[]) {
			return list.filter((item: any) => filters.every((filter: AdvancedSearchFilter) => {
				const filterKey = (filter.filterKey ? filter.filterKey : filter.key) +
					(filter.column && filter.column.dynamicField ? ".value" : "")
				const filterFieldValue = get(item, filterKey)

				switch (filter.operator) {
					case Operator.Equals:
						return filterFieldValue == filter.value // eslint-disable-line
					case Operator.Contains:
						if (filter.columnDataType === "list-multiple") {
							return filterFieldValue && Array.isArray(filterFieldValue) && filterFieldValue.find(i => i.id ? i.id === filter.value : i === filter.value)
						} else if (filter.columnDataType === "list-data") {
							return filterFieldValue && Array.isArray(filterFieldValue) && filterFieldValue.find(i => i.id === filter.value)
						} else {
							return filterFieldValue && filterFieldValue.toLowerCase().indexOf(filter.value!.toLowerCase()) > -1
						}
					case Operator.GreaterThan: return filterFieldValue != null && filterFieldValue > filter.value!
					case Operator.LessThan: return filterFieldValue != null && filterFieldValue < filter.value!
					case Operator.IsSet:
						if (filter.columnDataType === "list-multiple" || filter.columnDataType === "list-data") {
							return !!filterFieldValue && Array.isArray(filterFieldValue) && filterFieldValue.length > 0
						} else {
							return !!filterFieldValue
						}

					case Operator.NotSet:
						if (filter.columnDataType === "list-multiple" || filter.columnDataType === "list-data") {
							return !filterFieldValue || !Array.isArray(filterFieldValue) || filterFieldValue.length === 0
						} else {
							return !filterFieldValue
						}
				}

				return true
			}))
		},
		filterGroups (resultGroups: CalculationResults[], filterFunctions: ((record: CaseDetails) => boolean)[], labelFormat: LabelFormat, insight: Insight, fields: Field[]) : FilteredCalculationResults[] {
			if (!resultGroups) {
				return []
			}

			const unfilteredLengths: { [key: string]: number } = resultGroups.toDictionaryMapValue(c => c.value, c => c.results ? c.results.length : 0)

			if (!filterFunctions || filterFunctions.length === 0) {
				return resultGroups.map(resultGroup => {
					return {
						...resultGroup,
						unfilteredLength: unfilteredLengths[resultGroup.value]
					}
				})
			}

			const clientFilteredResults: CalculationResults[] =
				resultGroups.map(resultGroup => {
					return {
						...resultGroup,
						results: resultGroup.results
							? resultGroup.results.filter((record: CaseDetails) => filterFunctions.every(f => f(record)))
							: []
					}
				})

			const clientGroupedSearchResults: GroupedSearchResults = {
				labelFormat: labelFormat,
				results: clientFilteredResults.toDictionaryMapValue(c => c.value, c => c.results),
				labels: clientFilteredResults.toDictionaryMapValue(c => c.value, c => c.label)
			}

			const calculationRunner = new ClientSideCalculations()
			const clientCalculatedResults =	calculationRunner.runCalculations(clientGroupedSearchResults, insight, fields)

			calculationRunner.applyCalculationTotals(clientCalculatedResults, insight, [])
			return clientCalculatedResults.results.map(resultGroup => {
				return {
					...resultGroup,
					unfilteredLength: unfilteredLengths[resultGroup.value]
				}
			})
		}
	}
}
